/**
 * ## EditableTable.tsx ##
 * This file contains EditableTable component
 * @packageDocumentation
 */

import * as React from 'react';

import Table, { ColumnProps, TableProps, TablePaginationConfig } from 'antd/es/table';
import Checkbox from 'antd/es/checkbox';
import Dropdown from 'antd/es/dropdown';
import Tooltip, { TooltipPlacement } from 'antd/es/tooltip';
import { ColumnType, TableRowSelection } from 'antd/es/table/interface';
import Pagination, { PaginationProps } from 'antd/es/pagination';
import Spin from 'antd/es/spin';
import { PopoverProps } from 'antd/es/popover';

import Button from '@common/react/components/Forms/Button';
import {
	useItemsProviderContext,
	WithKey,
	SortingDirection,
	ColumnFilter,
	ItemsProviderContext,
	PaginationOptions,
} from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import CheckboxList from '@common/react/components/Core/CheckboxList/CheckboxList';
import CoreLoader from '@common/react/components/Core/LoaderProvider/Loader';
import ResizeObserverContainer from '@common/react/components/UI/ResizeObserverContainer/ResizeObserverContainer';
import TableButtons from '@common/react/components/UI/TableButtons/TableButtons';
import { useItemModalProviderContext } from '@common/react/components/Core/ItemModalProvider/ItemModalProvider';
import { useEditableTableSettingsContext } from '@common/react/components/Core/EditableTable/EditableTableSettingsProvider';
import { LoadingError } from '@common/react/components/UI/LoadingError/LoadingError';

import '@common/react/scss/components/editableTable.scss';

export const defaultPageSizeOptions = [10, 20, 30, 40];

/**
 * This is function type. Used to update item edit state.
 * The function accepts two argument - key and value.
 * key - property to be edited.
 * value - new value
 */
export type SetValueCallback = (key: string, value: any) => void;
/**
 * This is function type. Used to render field with validation wrapper.
 * The function accepts four argument - key, node, inputType and inputProps.
 * key - property to be edited.
 * node - custom field node.
 * inputType - input type property.
 * inputProps
 */
export type ValidationWrapperCallback = (
	key: string,
	node?: React.ReactNode,
	inputType?: string,
	inputProps?: React.HTMLProps<HTMLInputElement>
) => React.ReactNode;

export enum ColumnActions {
	Custom,
	Cancel,
	Remove,
	Edit,
	Save,
	Drag,
	Modal,
}

type EditableTablePaginationProps =
	Omit<PaginationProps, 'current' | 'pageSize' | 'itemRender' | 'onChange' | 'onShowSizeChange'>;

/**
 * @interface ViewColumnProps
 * @typeParam T - T Any WithKey entity
 */
export interface ViewColumnProps<T extends WithKey = any> {
	/**
	 * row index
	 */
	index: number;
	/**
	 * getDefaultAction - used for rendering actions
	 * @param actions - action array
	 * @returns ReactNode
	 */
	getDefaultAction: (actions: Array<ColumnActions>) => React.ReactNode;
	/**
	 * save - call save request without validation
	 * @param item - new item values
	 * @param saveRequest - request name
	 * @returns ReactNode
	 */
	save: (item?: T, saveRequest?: string) => void;
	/**
	 * current item loading state
	 */
	loading: boolean;
	/**
	 * update - change item state without request
	 * @param item - new item values
	 */
	update: (item: Partial<T>) => void;
	/**
	 * updateAndSave - call save request with validation
	 * @param item - new item values
	 * @param saveRequest - request name
	 * @returns ReactNode
	 */
	updateAndSave: (item: Partial<T>, saveRequest?: string) => void;
	/**
	 * reload - refresh table items
	 */
	reload: () => void;
	/**
	 * current row expanded or not
	 */
	expanded: boolean;
	/**
	 * callback to change current row expanded state
	 */
	setExpanded: (value: boolean) => void;
}

export interface EditColumnProps<T extends WithKey = any> extends ViewColumnProps<T> {
	/**
	 * setValue - used to update item edit state
	 */
	setValue: SetValueCallback;
	/**
	 * validationWrapper - return default field with validation wrapper
	 */
	validationWrapper: ValidationWrapperCallback;
	/**
	 * add - add new item
	 * @param item - new item values
	 */
	add: (item?: Partial<T>) => void;
	/**
	 * expand - change item expand state
	 * @param value - expand value
	 */
	expand: (value: boolean) => void;
}

interface ActionProps<T extends WithKey = any> {
	handleDelete: (record: T, index: number) => void;
	isDisabled: (record: T) => boolean;
	Loader: (record: T) => boolean;
}

export type EditableTableActonDelegate<Props, T extends WithKey = any> = (
	item: T, props: Props, actionsProps: ActionProps<T>,
) => React.ReactNode;

export type EditableTableActon<Props, T extends WithKey = any> = ColumnActions | EditableTableActonDelegate<Props, T>;

export type EditableTableActionsType<Props, T extends WithKey = any> = ((prev: Array<ColumnActions>) => Array<EditableTableActon<Props, T>>)
	| Array<EditableTableActon<Props, T>>;

export interface EditableTableColumn<T extends WithKey> extends Omit<ColumnProps<T>, 'render' | 'children' | 'dataIndex'> {
	dataIndex: string | number;
	/**
	 * view - cell render function in view mode
	 * @param record - row item
	 * @param props - ViewColumnProps
	 * @returns ReactNode
	 */
	view: (item: T, props: ViewColumnProps<T>) => React.ReactNode | void;
	/**
	 * view - cell render function in edit mode
	 * @param record - row item
	 * @param props - EditColumnProps
	 * @returns ReactNode
	 */
	edit?: (item: T, props: EditColumnProps<T>) => React.ReactNode | void;
	/**
	 * default state for settings
	 */
	hide?: boolean;
	/**
	 * have access to view the column. Default true
	 */
	enable?: boolean;
	/**
	 * custom settings title
	 */
	settingsTitle?: React.ReactNode;
	/**
	 * column name for mobile view
	 */
	mobileTitle?: string;
	/**
	 * view - custom render in mobile view in view mode
	 * @param record - row item
	 * @param props - ViewColumnProps
	 * @returns ReactNode
	 */
	mobileView?: (item: T, props: ViewColumnProps<T>) => React.ReactNode | void;
	/**
	 * view - custom render in mobile view in edit mode
	 * @param record - row item
	 * @param props - EditColumnProps
	 * @returns ReactNode
	 */
	mobileEdit?: (item: T, props: EditColumnProps<T>) => React.ReactNode | void;
	/**
	 * hide checkbox in columnSettings
	 */
	hideCheckbox?: boolean;
	children?: Array<EditableTableColumn<T>>;
}

export interface CustomTableRowSelection<T> extends Omit<TableRowSelection<T>, 'selections'> {
	selections?: Array<SelectionsOptions<T>>;
}

interface ExpandRowProps<T extends WithKey> extends EditColumnProps<T> {
	item: T,
	readonly: boolean,
	index: number,
	indent: number,
	expanded: boolean;
}

interface Expand<T extends WithKey> {
	/**
	 * render - render row expanded row
	 * @param ExpandRowProps - {item, setValue, readonly}
	 * @param index - row index
	 * @param indent - indent value
	 * @param expanded - row expand state
	 * @returns string
	 */
	render?: (expandRowProps: ExpandRowProps<T>) => React.ReactNode;
	/**
	 * show - show expand or not. defines the starting value.
	 * @param item - row data
	 * @returns ReactNode
	 */
	show?: (item: T, expandedRows: Array<string>) => boolean;
	/**
	 * expandIcon - custom expand icon
	 * @param props - antd props
	 * @param item - row data
	 * @returns ReactNode
	 */
	icon?: (props: any, item: T) => React.ReactNode;
	/**
	 * showIcon - show expand icon condition
	 * @param props - antd props
	 * @param item - row data
	 * @returns boolean
	 */
	showIcon?: (props: any, item: T) => boolean;
	/**
	 * destroy row id expanded is false
	 */
	destroyOnHide?: boolean;
	/**
	 * Enable row can be expandable
	 */
	expandable?: (item: T) => boolean;
	columnWidth?: string | number;
	columnTitle?: React.ReactNode;
	fixed?: 'left' | 'right';
}

export interface ActionColumnPropsType<T extends WithKey> extends Omit<ColumnProps<T>, 'render' | 'title' | 'dataIndex' | 'children'> {
	/**
	 * custom action settings title
	 */
	settingsTitle?: React.ReactNode;
	/**
	 * wrap children by div with wrapperClassName
	 */
	wrapperClassName?: string;
	/**
	 * use TableButtons or not. if true custom each function should return one dom element.
	 */
	adaptive?: boolean;
}

/**
 * EditableTable component props
 *
 * @interface EditableTableProps
 * @typeParam T - T Any WithKey entity
 */
export interface EditableTableProps<T extends WithKey> {
	/**
	 * column array
	 * @interface EditableTableColumn[]
	 * @typeParam T - T Any WithKey entity
	 */
	columns: Array<EditableTableColumn<T>>;
	/**
	 * table className
	 */
	className?: string;
	/**
	 * if true show modal confirmation before remove item
	 */
	removeConfirmation?: boolean;
	/**
	 * custom deletion confirmation message
	 */
	removeConfirmationText?: string;
	/**
	 * show border on not. default true
	 */
	bordered?: boolean;
	/**
	 * rowClassName - get row className
	 * @param record - row value
	 * @param index - row index
	 * @returns string
	 */
	rowClassName?: (record: T, index: number) => string;
	/**
	 * parameter defining the render in the action column header. default true
	 * - boolean - show or hide default button.
	 * - string - show default button with this string: addButton="Some text" will render <EditableTableAddButton>Some text</EditableTableAddButton>
	 * - React.ReactElement - used to override default button. for example, if you need to display a string in the action column header,
	 * pass addButton={<>Some string</>}
	 * - function - override default button and call some actions from context.
	 *
	 * @example default
	 * addButton={true} // by default
	 *
	 * @example hide button
	 * addButton={false}
	 *
	 * @example default button with custom text
	 * addButton="custom text" // will render <EditableTableAddButton>custom text</EditableTableAddButton>
	 *
	 * @example show only text without button
	 * addButton={<>text</>}
	 *
	 * @example custom components
	 * addButton={({ actions: { addItem } }) => <SomeCustomComponent onClick={() => addItem()}/>}
	 * addButton={({ actions: { reload } }) => <button onClick={() => reload()}>Refresh</button>}
	 */
	addButton?: ((context: ItemsProviderContext<T>) => React.ReactNode) | React.ReactNode | string | boolean;
	/**
	 * view mode
	 */
	readonly?: boolean;
	/**
	 * prevent request after change pagination or sorting
	 */
	localReload?: boolean;
	/**
	 * prevent request when delete item
	 */
	localRemove?: boolean;
	/**
	 * view actions array.
	 * array of functions or ColumnActions
	 *
	 * if function have two arguments - item: row value, props: ViewColumnProps
	 *
	 * you can use built-in buttons by passing ColumnActions
	 * - [ColumnActions.Edit, ColumnActions.Remove]
	 *
	 * if you need custom buttons, use the function
	 * - [(item, props, {handleDelete, isDisabled, Loader}) => React.ReactNode]
	 *
	 * or both
	 * - [ColumnActions.Edit, (item, props, {handleDelete, isDisabled, Loader}) => React.ReactNode]
	 *
	 * or if you need default buttons and some custom use like this
	 * - (defaultActions) => [...defaultActions, (item, props, {handleDelete, isDisabled, Loader}) => React.ReactNode]
	 */
	viewActions?: EditableTableActionsType<ViewColumnProps<T>, T>;
	/**
	 * edit actions array.
	 *
	 * array of functions or ColumnActions
	 * if function have two arguments - item: row value, props: ViewColumnProps
	 *
	 * you can use built-in buttons by passing ColumnActions
	 * - [ColumnActions.Edit, ColumnActions.Remove]
	 *
	 * if you need custom buttons, use the function
	 * - [(item, props, {handleDelete, isDisabled, Loader}) => React.ReactNode]
	 *
	 * or both
	 * - [ColumnActions.Edit, (item, props, {handleDelete, isDisabled, Loader}) => React.ReactNode]
	 *
	 * or if you need default buttons and some custom use like this
	 * - (defaultActions) => [...defaultActions, (item, props, {handleDelete, isDisabled, Loader}) => React.ReactNode]
	 */
	editActions?: EditableTableActionsType<EditColumnProps<T>, T>;
	/**
	 * hide table pagination. Default false
	 */
	hidePagination?: boolean;
	/**
	 * show 'select row' column
	 */
	withSelectedRow?: boolean;
	/**
	 * antd selectionRow props
	 */
	selectionRow?: CustomTableRowSelection<T>;
	/**
	 * antd TooltipPlacement property. Used in SelectionRow tooltip
	 */
	selectionTooltipPlacement?: TooltipPlacement;
	/**
	 * buttons inside SelectionRow tooltip
	 */
	columnTitleButtons?: (selectedItems: Array<T>, setSelectedItems?: (items: Array<T>) => void) => React.ReactNode;
	/**
	 * hide SelectedRow delete action
	 */
	withoutSelectedDelete?: boolean;
	/**
	 * custom antd props
	 */
	antdProps?: TableProps<T>;
	/**
	 * show column settings. Default true
	 */
	withColumnSettings?: boolean;
	/**
	 * defaultColumnSettings - get default column settings. string array or function.
	 * if function accepts one argument - column data
	 */
	defaultColumnSettings?: ((array: Array<EditableTableColumn<T>>) => Array<string | never>) | Array<string>;
	/**
	 * custom table loader, by default <Loader defaultLoader={<Spin />} type="tableLoader" />
	 */
	loader?: React.ReactNode;
	/**
	 * show only saved items in view mode
	 */
	onlySavedItems?: boolean;
	/**
	 * removeConfirmationContainer - get confirmation modal container
	 * @param instance - trigger html node
	 */
	removeConfirmationContainer?: (instance?: React.ReactInstance) => HTMLElement;
	/**
	 * onEdit - item edit callback
	 * @param instance - trigger html node
	 */
	onEdit?: (item: T) => void;
	/**
	 * use mobileView. by default true
	 */
	withMobileView?: boolean;
	/**
	 * renderMobile - custom render mobile view
	 * @param caption - column name
	 * @param text - cell value
	 */
	renderMobile?: typeof renderMobileCell;
	/**
	 * onColumnSettingsChange - column settings change callback
	 * @param columnSettings - new column settings
	 */
	onColumnSettingsChange?: (columnSettings: Array<string>) => void;
	/**
	 * table id. used for tests
	 */
	tableId?: string;
	/**
	 * reset columnSettings after changing number of columns
	 */
	resetColumnSettings?: boolean;
	/**
	 * sync columnSettings. overwrite table columnSettings
	 */
	columnSettings?: Array<string>;
	/*
	* show Clear All link. required removeAllRequest at ItemsProvider
	 */
	withClearAll?: boolean;
	/**
	 * show items total count
	 */
	withTotal?: boolean;
	/**
	 * antd props for action column
	 */
	actionColumnProps?: ActionColumnPropsType<T>;
	/**
	 * antd props for column settings popover
	 */
	columnSettingsPopoverProps?: PopoverProps;
	/**
	 * antd pagination props
	 */
	paginationProps?: EditableTablePaginationProps;
	/**
	 * custom error message
	 * @param error - error message
	 * @param reload - load data callback
	 */
	customError?: (error: string, reload: () => void) => React.ReactNode;
	/**
	 * custom row key
	 * @param item - current row data
	 * @param type - itemsProvider type property
	 */
	getKey?: (item: T & {keyGUID?: string}, type: string) => string;
	/**
	 * a function that is called after the table has been resized
	 * @param props - data about the new table size
	 */
	onResize?: (props: { width: number, height: number }) => void;
	/**
	 * determines whether filters need to be reset after adding a new element
	 *
	 * @default true
	 */
	resetFiltersAfterAdd?: boolean;
	/**
	 * row expand option
	 */
	expand?: Expand<T>;
	/**
	 * Set props on per row
	 */
	onRow?: (data: T, index?: number) => React.HTMLAttributes<any> & React.TdHTMLAttributes<any>;
	/**
	 * Set props on per row
	 */
	customRow?: (props: any) => React.ReactNode
	/**
	 * custom Locale
	 */
	locale?: TableProps<T>['locale'];
	showHeader?: boolean;
	loading?: boolean;
}

const emptyColumns = [{
	key: '-',
	title: '-',
	render: () => '-',
}];

const defaultError = (_error: string, reload: () => void) => <LoadingError reload={reload} />;

/**
 * getSortOrder - return column sorting
 * @param column - {caption, direction}
 * @param dataIndex - column sorting name
 * @returns res
 */
const getSortOrder = (column: Array<ColumnFilter> | undefined, dataIndex: string | number): 'ascend' | 'descend' | undefined => {
	const sorter = column?.find(({ caption }) => caption === dataIndex);

	return sorter
		? +sorter.direction === SortingDirection.Descending ? 'descend' : 'ascend'
		: undefined;
};

/**
 * renderMobileCell - return cell markup for mobile
 * @param caption - column name
 * @param text - cell value
 * @param fullWidth - use full cell width
 * @returns res
 */
export const renderMobileCell = (caption: React.ReactNode, text: string | JSX.Element | null | Array<string>, fullWidth = false): JSX.Element => {
	return <>
		<div className={`table-mobile__caption ${fullWidth ? 'table-mobile__caption_full' : ''}`}>{caption}</div>
		<div className={`table-mobile__content ${fullWidth ? 'table-mobile__content_full' : ''}`}>{text}</div>
	</>;
};

/**
 * getDefaultColumnSettings - filter columns by !hide and return dataIndex from column
 * @param columns - table columns
 * @returns res
 */
export const getDefaultColumnSettings = <T extends WithKey, >(columns: Array<EditableTableColumn<T>>) =>
	columns.filter(({ hide }) => !hide).map(({ dataIndex }) => dataIndex);

const itemRender = (tableId: string) => (_item: any, type: string, originalElement: React.ReactNode) => {
	if (type === 'prev') {
		return <div data-pagination={`prev-${tableId}`} style={{ height: '100%' }}>{originalElement}</div>;
	}
	if (type === 'next') {
		return <div data-pagination={`next-${tableId}`} style={{ height: '100%' }}>{originalElement}</div>;
	}
	return originalElement;
};

interface SelectionsOptions<T> {
	key: string;
	label: string;
	onSelect: (items: Array<T>, selectedRows: Array<T>, setSelectedRows: React.Dispatch<React.SetStateAction<Array<T>>>) => void;
}

const defaultSelections: Array<SelectionsOptions<any>> = [
	{
		key: 'all',
		label: 'Select all data',
		onSelect: (items, _selectedRows, setSelectedRows) => {
			setSelectedRows(items);
		},
	},
	{
		key: 'invert',
		label: 'Invert current page',
		onSelect: (items, selectedRows, setSelectedRows) => {
			setSelectedRows(items.filter((item) => !selectedRows.find((row) => row.id === item.id)));
		},
	},
	{
		key: 'none',
		label: 'Clear all data',
		onSelect: (_items, _selectedRows, setSelectedRows) => {
			setSelectedRows([]);
		},
	},
	{
		key: 'odd',
		label: 'Select Odd Row',
		onSelect: (items, _selectedRows, setSelectedRows) => {
			setSelectedRows(items.filter((_, index) => {
				return index % 2 === 0;
			}));
		},
	},
	{
		key: 'even',
		label: 'Select Even Row',
		onSelect: (items, _selectedRows, setSelectedRows) => {
			setSelectedRows(items.filter((_, index) => {
				return index % 2 !== 0;
			}));
		},
	},
];

const defaultGetKey = <T extends WithKey, >(item: T, type: string) => `${type}-${item.id}`;

/**
 * getKeyWithGUID - return row key
 * @param item - row item
 * @param type - ItemsProvider type
 * @returns res
 */
export const getKeyWithGUID = <T extends WithKey, >(item: T, type: string) => `${type}-${item.id}-${item?.keyGUID || ''}`;

interface EditableTableAddButtonProps {
	className?: string;
	children?: any;
}

const CanChange = (multiple: boolean, editCount: number) => {
	return multiple || editCount === 0;
};

/**
 * EditableTableAddButton component.
 *
 * @param props - EditableTableAddButtonProps
 * @type {React.FC<EditableTableAddButtonProps>}
 * @returns React.ReactElement
 */
export const EditableTableAddButton: React.FC<EditableTableAddButtonProps> = (props) => {
	const {
		className = 'btn btn-sm btn-primary pull-right editable-table-add-btn',
		children = <i className="fa fa-plus" />,
	} = props;

	const context = useItemsProviderContext();

	const {
		state: {
			edits, multiple,
		},
		actions: {
			addItem,
		},
	} = context;

	const editCount = Object.keys(edits).length;

	const handleAdd = () => {
		if (CanChange(multiple, editCount)) {
			addItem();

			// setEdits({ ...edits, [newItem.id]: newItem });
		}
	};

	return (
		<button
			className={className}
			type="button"
			title="Add Item"
			onClick={handleAdd}
			disabled={multiple ? false : !!editCount}
		>
			{children}
		</button>
	);
};

/**
 * EditableTable component.
 * - need fontawesome icon styles and $width-sm variable at '@app/scss/utils/variables.scss'
 * - if error with types at some property try to add generic
 *
 * @typeParam T - T Any {WithKey}
 * @param props - EditableTableProps<T>
 * @type {React.FC<EditableTableProps>}
 * @returns React.ReactElement
 */
export const EditableTable: <U extends WithKey = never, T extends U = U>(
	p: EditableTableProps<T>,
) => React.ReactElement<T> = <U extends WithKey = never, T extends U = U>(
	props,
) => {
	const settings = useEditableTableSettingsContext().state || {};
	const {
		columns,
		removeConfirmation = true,
		bordered = settings.bordered ?? true,
		rowClassName,
		addButton = settings.addButton ?? true,
		readonly = false,
		expand,
		localReload = false,
		localRemove = false,
		viewActions = ((p) => p) as EditableTableActionsType<ViewColumnProps>,
		editActions = ((p) => p) as EditableTableActionsType<EditColumnProps>,
		hidePagination = false,
		withSelectedRow = false,
		selectionRow = {},
		columnTitleButtons,
		withoutSelectedDelete = false,
		antdProps = {},
		selectionTooltipPlacement = 'top',
		withColumnSettings = true,
		defaultColumnSettings = getDefaultColumnSettings as (Array<string> | ((array: Array<EditableTableColumn<T>>) => Array<string>)),
		removeConfirmationText = '',
		removeConfirmationContainer,
		loader = <CoreLoader defaultLoader={<Spin />} type="tableLoader" />,
		onlySavedItems = true,
		onEdit = (item) => item,
		withMobileView = true,
		renderMobile = settings.renderMobile || renderMobileCell,
		onColumnSettingsChange,
		tableId = '',
		resetColumnSettings = true,
		withTotal = false,
		withClearAll = false,
		columnSettings: propsColumnSettings,
		columnSettingsPopoverProps,
		actionColumnProps,
		paginationProps = {} as EditableTablePaginationProps,
		customError = settings.customError || defaultError,
		getKey: getKeyProp = defaultGetKey,
		onResize,
		resetFiltersAfterAdd = true,
		className = settings.className || '',
		onRow,
		customRow,
		locale,
		showHeader,
		loading: propsLoading,
	}: EditableTableProps<T> = props;
	const [key] = React.useState(() => Math.random());
	const {
		adaptive,
		wrapperClassName = '',
		...actionColumnPropsRest
	} = { ...settings.actionColumnProps, ...actionColumnProps };
	const {
		show: expandShowProps,
		render: expandedRowRender,
		icon: expandIcon,
		showIcon: showExpandIcon = () => true,
		destroyOnHide: destroyExpandedOnHide = true,
		expandable: rowExpandable,
		columnWidth: expandableColumnWidth,
		columnTitle: expandableColumnTitle,
		fixed: expandableFixed,
	} = expand || {};
	const showSizeChanger = (paginationProps.showSizeChanger ?? true);
	const _viewActions = typeof viewActions === 'function' ? viewActions([ColumnActions.Edit, ColumnActions.Remove]) : viewActions;
	const _editActions = typeof editActions === 'function' ? editActions([ColumnActions.Cancel, ColumnActions.Save]) : editActions;

	const [afterLoadAdd, setAfterLoadAdd] = React.useState(false);
	const [expandedRows, setExpandedRows] = React.useState<Array<string>>([]);

	const itemsProviderContext = useItemsProviderContext<T>();
	const {
		state: {
			items, advancedItems, loading, pagination, edits, errors, loaders, multiple, loadRequest, filters, addedFirst, transformAfterSave,
			deleting, removeAllRequest, error, selectedRows,
		},
		actions: {
			addItem, save, updateItems, setEdits, setEdit, reload, removeItems, setItems, validateAll,
			load, remove, setSelectedRows,
		},
	} = itemsProviderContext;
	const [_pageSizeOptions] = React.useState(() => {
		return Array.from(new Set(defaultPageSizeOptions.concat(pagination.pageSize).filter((item) => item))).sort((a, b) => a - b);
	});
	const pageSizeOptions = paginationProps.pageSizeOptions || _pageSizeOptions;

	const modalContext = useItemModalProviderContext<T>();

	const editCount = Object.keys(edits).length;
	React.useEffect(() => {
		if (editCount > 0 && selectedRows.length) {
			setSelectedRows([]);
		}
	}, [editCount]);
	const getKey = React.useMemo(() => (item: T) => getKeyProp(item, loadRequest || `${key}`), [loadRequest]);

	const expandShow = (item: T, expandedRows: Array<string>) => {
		return expandShowProps ? expandShowProps(item, expandedRows) : expandedRows.includes(getKey(item));
	};

	const handleCheckedChange = () => {
		setSelectedRows(selectedRows.length !== items.length ? items : []);
	};

	const defaultConfirmationProps = {
		title: 'Selected items will be deleted. Are you Sure?',
		getContainer: removeConfirmationContainer,
		okButtonProps: {
			'data-confirm': 'all',
		},
		okText: 'Yes',
		okType: 'danger',
		cancelText: 'Cancel',
		className: 'editable-table-delete-modal',
	};

	const handleDeleteItems = () => removeItems(selectedRows, undefined, defaultConfirmationProps);

	const rowSelection = {
		onChange: (_selectedRowKeys: any, selectedRows: Array<T>) => {
			setSelectedRows(selectedRows);
		},
		selectedRowKeys: selectedRows.map(getKey),
		columnTitle: <div className="selected-items__actions">
			<Tooltip
				open={selectedRows.length > 0 && !editCount && !!(columnTitleButtons || !withoutSelectedDelete)}
				placement={selectionTooltipPlacement}
				getTooltipContainer={(node) => node.closest('.ant-table') || document.body}
				title={<>
					{withoutSelectedDelete ? null : <Button
						key="remove"
						className="btn btn-sm btn-danger"
						type="button"
						disabled={selectedRows.length === 0}
						title="Delete Selected Rows"
						onClick={handleDeleteItems}
					>
						<i className="fa fa-trash" />
					</Button>}
					{columnTitleButtons && columnTitleButtons(selectedRows, setSelectedRows)}
				</>}
			>
				<Checkbox
					disabled={!!editCount}
					indeterminate={selectedRows.length > 0 && selectedRows.length < items.length}
					checked={selectedRows.length === items.length}
					onChange={handleCheckedChange}
				/>
			</Tooltip>
			<div className="ant-table-selection-extra">
				<Dropdown
					disabled={!!editCount}
					menu={{
						items: ((selectionRow as CustomTableRowSelection<T>)?.selections || defaultSelections)
							.map((item: SelectionsOptions<T>) => ({
								...item,
								onSelect: undefined,
								onClick: () => item.onSelect(items, selectedRows, setSelectedRows),
							})),
					}}
				>
					<i className="fa fa-angle-down" />
				</Dropdown>
			</div>
		</div>,
		placement: 'left',
		...selectionRow,
		getCheckboxProps: editCount > 0
			? (record: T) => ({ ...selectionRow.getCheckboxProps?.(record), disabled: true }) : selectionRow.getCheckboxProps,
	};

	const onChange = (pagination: PaginationOptions & { column?: Array<ColumnFilter> }) => {
		!localReload && reload(pagination)
			.then(() => {
				deleting.current = false;
			});
	};

	const Loader = (record: T) => {
		return loaders[record.id];
	};

	const Edit = (record: T) => {
		return readonly ? undefined : edits[record.id] as T;
	};

	const Error = (record: T, path: string) => {
		const item = errors[record.id]?.err;
		return typeof item !== 'undefined' ? item[path] : null;
	};

	const clearEdit = (id: number) => {
		const temp = { ...edits };
		delete temp[id];
		setEdits(temp);
	};

	const setItemPropValue = (id: number, propName: string, value: any) => {
		if (typeof value === 'function') {
			edits[id] = { ...edits[id], [propName]: value(edits[id][propName]) };
		} else {
			edits[id] = { ...edits[id], [propName]: value };
		}
		onEdit && onEdit(edits[id]);
		setEdit(edits[id]);
		validateAll(false)
			.catch(console.log);
	};

	const deleteFromArray = (index: number) => {
		const t = [...(advancedItems || items)];
		t.splice(index, 1);
		updateItems(t);
		// setItems(t);
	};

	const saveInArray = (id: number, response: T, resetEdits?: boolean) => {
		updateItems((advancedItems || items).map((q) => (q.id === id
			? {
				...q,
				...transformAfterSave({
					prevItem: q,
					item: { ...edits[id], id: response.id },
					response,
				}),
			} : q)), resetEdits);
		// setItems(_items.map(q => q.id === record.id ? { ...q, ...edits[record.id] } : q));

		clearEdit(id);

		// setEdits({ ...edits, [record.id]: undefined });
	};

	const afterRemove = (id: number) => {
		if (!multiple) {
			clearEdit(id);

			if (pagination.current === 1 && pagination.total === 1) {
				setItems((advancedItems || items).filter((q) => q.id !== id));
			} else if (items.length === 1) {
				onChange({ current: pagination.current - 1 });
			} else {
				reload({ current: pagination.current })
					.then(() => {
						deleting.current = false;
					});
			}
		} else {
			setItems((advancedItems || items).filter((q) => q.id !== id));

			deleting.current = false;
		}
	};

	const saveItem = (record: T, skipValidation?: boolean, saveRequest?: string) => {
		if (record.deleted) {
			deleting.current = true;
		}
		save(record, skipValidation, saveRequest).then((response) => {
			if (response) {
				if (record.deleted) {
					afterRemove(record.id);
				} else if (!multiple) {
					clearEdit(record.id);

					if (record.id > 0) {
						onChange({ current: pagination.current });
					} else if (resetFiltersAfterAdd) {
						const lastPage = Math.ceil(pagination.total / pagination.pageSize);
						load(
							{ page: addedFirst ? 1 : lastPage, pageSize: pagination.pageSize },
							{ resetFilters: true },
						);
					} else {
						load({ page: pagination.current, pageSize: pagination.pageSize }, { silent: true });
					}
				} else {
					const newEdits = JSON.parse(JSON.stringify({ ...edits }));
					delete newEdits[record.id];
					saveInArray(record.id, response, false);
					load({}, { edits: newEdits });
				}
			}
		}).catch(() => {
			if (record.deleted) {
				record.deleted = false;
				deleting.current = false;
			}
		});
	};

	const _removeItem = (record: T, index: number) => {
		if (record.id > 0) {
			deleting.current = true;

			remove(record, removeConfirmation
				? {
					...defaultConfirmationProps,
					title: removeConfirmationText || 'Item will be deleted. Are you Sure?',
					okButtonProps: {
						'data-confirm': `${record.id}`,
					},
				}
				: null).then(() => {
				afterRemove(record.id);
			}).catch(() => {
				record.deleted = false;
				deleting.current = false;
			});
		} else {
			deleteFromArray(index);
		}
	};

	const updateItem = (item: Partial<T>, id: number) => {
		setItems((advancedItems || items).map((el) => (el.id === id ? { ...el, ...item, id } : el)));
	};

	const handleDelete = (record: T, index: number) => {
		if (CanChange(multiple, editCount)) {
			if (record.id < 0) {
				handleCancel(record);
			} else if (localRemove) {
				const item = Edit(record);
				if (item) {
					item.deleted = true;
					updateItems((advancedItems || items).map((q) =>
						(q.id === record.id ? { ...q, ...edits[record.id] } : q)));
				}
			} else {
				_removeItem(record, index);
			}
		}
	};

	const handleEdit = (record: T) => {
		if (CanChange(multiple, editCount)) {
			setEdit({ ...record });
		}

		// setEdits({ ...edits, [record.id]: { ...record } });
	};

	const handleCancel = (record: T) => {
		if (record.id < 0) {
			// setItems(_items.filter(q => q.id !== record.id));
			updateItems((advancedItems || items).filter((q) => q.id !== record.id));

			if (items.length === 1 && pagination.current > 1) {
				onChange({ current: pagination.current - 1 });
			}
		}

		clearEdit(record.id);

		// setEdits({ ...edits, [record.id]: undefined });
	};

	const handleSave = (record: T, skipValidation?: boolean, saveRequest?: string) => {
		saveItem(record, skipValidation, saveRequest);
	};

	const isDisabled = (record: T) => {
		return multiple ? false : !!editCount && (edits[record.id]?.id !== record.id);
	};

	const actionsDictionary = {
		[ColumnActions.Modal]: (item: T) => modalContext?.state && <Button
			isLoading={modalContext.state.id === item?.id && modalContext.state.loading}
			key="modal"
			className="btn btn-sm btn-default"
			type="button"
			title={modalContext.state.buttonTitle}
			onClick={() => modalContext.actions.openModal(item)}
		>
			{modalContext.state.buttonChildren}
		</Button>,
		[ColumnActions.Cancel]: (item: T) => <button
			key="cancel"
			className="btn btn-sm btn-default"
			type="button"
			title="Cancel"
			onClick={() => handleCancel(item)}
		>
			<i className="fa fa-times" />
		</button>,
		[ColumnActions.Edit]: (item: T) => <button
			key="edit"
			className="btn btn-sm btn-default"
			type="button"
			title="Edit"
			onClick={() => handleEdit(item)}
			disabled={isDisabled(item)}
		>
			<i className="fa fa-pencil" />
		</button>,
		[ColumnActions.Save]: (item: T) => <Button
			key="save"
			className="btn btn-sm btn-primary"
			type="button"
			title="Save"
			onClick={() => handleSave(item)}
			isLoading={Loader(item)}
		>
			<i className="fa fa-save" />
		</Button>,
		[ColumnActions.Remove]: (item: T, props: ViewColumnProps) => {
			return <Button
				key="remove"
				className="btn btn-sm btn-danger"
				type="button"
				title="Delete"
				onClick={() => handleDelete(item, props.index)}
				isLoading={Loader(item)}
				disabled={isDisabled(item)}
			>
				<i className="fa fa-trash" />
			</Button>;
		},
	};

	const showActions = (
		actions: Array<EditableTableActon<EditColumnProps<T>, T>>,
		item: T,
		props: ViewColumnProps | EditColumnProps,
		withoutContainer?: boolean,
	) => {
		const actionsElements = actions.map((q: any) => (typeof q === 'function'
			? q(item, props, { handleDelete, isDisabled, Loader })
			: actionsDictionary[q](item, props)));
		const resElements = adaptive
			? (
				<TableButtons
					record={item}
					buttons={actionsElements.filter((item: any) => !!item).map((node: React.ReactNode) => ({
						visible: true,
						node,
					}))}
				/>)
			: actionsElements;

		return (!withoutContainer ? <div
			className={wrapperClassName || ''}
		>
			{resElements}
		</div>
			: resElements);
	};

	const actionColumn: EditableTableColumn<T> = {
		settingsTitle: 'Actions',
		...actionColumnPropsRest,
		title: addButton
			? (typeof addButton === 'string' || typeof addButton === 'boolean'
				? () => {
					return <EditableTableAddButton>
						{typeof addButton === 'string' ? addButton : undefined}
					</EditableTableAddButton>;
				}
				: typeof addButton === 'function' ? addButton(itemsProviderContext) : addButton)
			: '',
		className: `${addButton ? 'action-column' : ''} ${actionColumnPropsRest.className || ''}`,
		dataIndex: 'operation',
		view: (item: T, props: ViewColumnProps) => showActions(_viewActions, item, props),
		edit: (item: T, props: EditColumnProps) => showActions(_editActions, item, props),
	};

	const mergedColumns = readonly ? columns : columns.concat(actionColumn);

	const [columnSettings, setSettings] = React.useState<Array<string>>(Array.isArray(defaultColumnSettings)
		? defaultColumnSettings : defaultColumnSettings(mergedColumns));

	React.useEffect(() => {
		if (resetColumnSettings) {
			let newSettings = (Array.isArray(defaultColumnSettings)
				? defaultColumnSettings : defaultColumnSettings(mergedColumns));
			newSettings = newSettings.concat(!readonly && !newSettings.includes('operation') ? 'operation' : []);

			if (`${newSettings}` !== `${columnSettings}`) {
				setSettings(newSettings);
			}
		}
	}, [mergedColumns.length]);

	React.useEffect(() => {
		if (propsColumnSettings) {
			setSettings(propsColumnSettings);
		}
	}, [propsColumnSettings]);

	const getValidationWrapper = (item: T) => (
		key: string,
		node: React.ReactNode,
		inputType?: string,
		inputProps?: React.HTMLProps<HTMLInputElement>,
	) => {
		const error = Error(item, key);
		const showError = error && (errors[item?.id as number]?.submitCount || 0) > 0;

		return <div className={showError ? 'has-error' : ''}>
			<div className="is-relative" key={getKey(item)}>
				{node || <input
					className="form-control"
					type={`${inputType || 'text'}`}
					value={item[key]}
					onChange={(event) => {
						setItemPropValue(item?.id as number, key, event.target.value);
					}}
					{...inputProps}
				/>
				}
				{showError ? <div className="validation-message">{error}</div> : ''}
			</div>
		</div>;
	};

	const convertColumns = (columns: Array<EditableTableColumn<T>>): Array<ColumnType<T>> => {
		return columns.filter(({ enable }) => enable === undefined || enable).map((q) => {
			let dataIndex = q.dataIndex;

			if (!dataIndex && typeof q.title === 'string') {
				dataIndex = q.title as string;
			}
			const { column } = filters;

			return {
				...q,
				children: q.children ? convertColumns(q.children) : undefined,
				dataIndex,
				className: q.className || undefined,
				width: q.width || undefined,
				sortOrder: getSortOrder(column, dataIndex),
				render: (_, record, index) => {
					const item = Edit(record);
					const mainProp: ViewColumnProps<T> = {
						index,
						expanded: expandedRows.includes(getKey((item || record))),
						loading: loaders[record.id],
						update: (values) => updateItem(values, record.id),
						save: (values, saveRequest) => handleSave(values || record, false, saveRequest),
						updateAndSave: (values, saveRequest) => handleSave(
							{
								...record, ...item, ...values, id: record.id,
							},
							true,
							saveRequest,
						),
						setExpanded: (value) => setExpandedRows((prev) => {
							const key = getKey((item || record));
							return value
								? prev.includes(key) ? prev : prev.concat(key)
								: prev.filter((id) => id !== key);
						}),
						reload: () => reload({ current: pagination.current }),
						getDefaultAction: () => <></>,
					};
					mainProp.getDefaultAction = (actions) => showActions(actions, record, mainProp, true);

					let view = q.view;
					let edit = q.edit;
					let needWrap = withMobileView;

					if (withMobileView) {
						if (typeof item !== 'undefined') {
							if (q.mobileEdit) {
								edit = q.mobileEdit;
								needWrap = false;
							}
						} else if (q.mobileView) {
							view = q.mobileView;
							needWrap = false;
						}
					}
					const column = q.title && typeof q.title === 'string' ? q.title || q.settingsTitle : dataIndex;

					let innerNode: React.ReactNode;

					if (typeof item !== 'undefined') {
						if (edit) {
							const props: EditColumnProps<T> = {
								...mainProp,
								setValue: (key: string, value) => setItemPropValue(item.id, key, value),
								validationWrapper: getValidationWrapper(item),
								add: addItem,
								expand: (value: boolean) => {
									if (typeof expandedRowRender !== 'undefined') {
										const key = getKey(item);
										setExpandedRows((prev) => (value
											? prev.includes(key) ? prev : [...prev, key]
											: prev.includes(key) ? prev.filter((k) => k !== key) : prev));
									}
								},
							};

							props.getDefaultAction = (actions) => showActions(actions, record, props, true);

							innerNode = edit(item, props);
						} else {
							innerNode = view(item, mainProp);
						}
					} else {
						innerNode = view(record, mainProp);
					}

					const node = <div data-column={column}>{innerNode}</div>;

					return needWrap ? renderMobile(q.mobileTitle || q.settingsTitle || q.title, node ?? '-') : node;
				},
			};
		});
	};

	const settingsFilter = (columns: Array<EditableTableColumn<T>>): Array<EditableTableColumn<T>> => {
		return withColumnSettings
			? columns.filter(({ dataIndex }) => !!columnSettings.find((i) => i === dataIndex))
			: columns;
	};

	const onPaginationChange = (current: number, pageSize: number) => {
		onChange({ current, pageSize });
	};

	const clearAllHandler = () => removeItems(
		items,
		undefined,
		{
			...defaultConfirmationProps,
			title: 'All items will be deleted. Are you Sure?',
		},
		true,
	);

	React.useEffect(() => {
		if (afterLoadAdd) {
			setAfterLoadAdd(false);
			addItem();
		}
	}, [pagination]);

	React.useEffect(() => {
		!readonly && !columnSettings.includes('operation') && setSettings((prev) => prev.concat('operation'));
	}, [readonly]);

	const resColumns = convertColumns((settingsFilter(mergedColumns)));

	const resultItems = React.useMemo(() => {
		const res = readonly && onlySavedItems ? items.filter(({ id }) => id > 0) : items;
		return localRemove ? res.filter((q) => !q.deleted) : res;
	}, [items, readonly, localRemove]);

	const components = React.useMemo(() => {
		return {
			...antdProps?.components,
			body: {
				...antdProps?.components?.body,
				row: ({ index, ...props }) => {
					const record = props?.children?.[0]?.props?.record;
					const newProps = { index, ...props, 'data-id': record?.id };
					const row = customRow || (antdProps?.components?.body as any)?.row;

					return row ? row(newProps) : <tr key={props['data-row-key']} {...newProps}>
						{props?.children}
					</tr>;
				},
			},
		};
	}, [antdProps?.components, customRow]);

	let tableProps: TableProps<T> = {
		bordered,
		dataSource: error ? [] : resultItems,
		columns: resColumns.length > 0
			? withSelectedRow && rowSelection.placement === 'right' ? [...resColumns].reverse() : resColumns
			: emptyColumns,
		rowClassName,
		onChange: (pagination: TablePaginationConfig, _: any, sorter: any) => {
			let column: Array<ColumnFilter> | undefined;

			// for multiple sort. supported since v4 version of antd
			/* if (sorter.column) {
				const newSorter = [{
					caption: sorter.field,
					direction: sorter.order === 'descend' ? SortingDirection.Descending : SortingDirection.Ascending
				}];
				column = filters.column ?
					filters.column
						.filter(({caption}) => caption !== sorter.field)
						.concat(newSorter)
					: newSorter;
			} else {
				column = filters.column ? filters.column.filter(({caption}) => caption !== sorter.field) : undefined;
			} */

			if (sorter.column) {
				column = [{
					caption: sorter.field,
					direction: sorter.order === 'descend' ? SortingDirection.Descending : SortingDirection.Ascending,
				}];
			} else {
				column = undefined;
			}

			onChange && onChange({
				pageSize: pagination.pageSize,
				current: pagination.current,
				column,
			}/* , filters, sorter, extra */);
		},
		...antdProps,
		showHeader,
		onRow,
		expandable: {
			...antdProps.expandable,
			rowExpandable: rowExpandable || antdProps?.expandable?.rowExpandable
				? (record: T) => !!(rowExpandable || antdProps.expandable?.rowExpandable)?.(Edit(record) || record)
				: undefined,
		},
		className: `${className || antdProps?.className || ''} ${withSelectedRow
			? `${rowSelection.placement === 'right' ? 'table-with-selected-rows' : ''}`
			: ''} ${withMobileView ? 'table-mobile' : ''}`,
		loading: loader ? { indicator: loader as React.ReactElement<HTMLElement>, spinning: propsLoading || loading } : propsLoading || loading,
		components,
		id: tableId || loadRequest || `${loadRequest}`,
		locale: {
			...(locale || antdProps?.locale),
			...(error ? { emptyText: customError(error, reload) } : {}),
		},
	};

	if (expandedRowRender) {
		React.useEffect(() => {
			const temp: Array<string> = [];

			for (let i = 0; i < resultItems.length; i++) {
				if (expandShow(resultItems[i], expandedRows)) {
					temp.push(getKey(resultItems[i]));
				}
			}

			setExpandedRows(temp);
		}, [resultItems]);

		const expandedRender: (options: ExpandRowProps<T>) => React.ReactNode = expandedRowRender
			|| (() => '');
		const _expandIcon = expandIcon || ((props, item) =>
			(showExpandIcon(props, item)
				? <a style={{ color: 'black' }} data-action="Expand" onClick={(e) => { props.onExpand(props.record, e); }}>
					<i className={`fa fa-${props.expanded ? 'minus' : 'plus'}-square-o`} aria-hidden="true" />
				</a>
				: <></>));

		tableProps = {
			...tableProps,
			expandable: {
				rowExpandable: rowExpandable || antdProps?.expandable?.rowExpandable
					? (record) => !!(rowExpandable || antdProps.expandable?.rowExpandable)?.(Edit(record) || record)
					: undefined,
				expandedRowRender: (record: T, index: number, indent: number, expanded: boolean) => {
					if (destroyExpandedOnHide && !expanded) {
						return null;
					}
					const item = Edit(record);
					const props = {
						index,
						indent,
						expanded,
						loading: loaders[record.id],
						update: (values: Partial<T>) => updateItem(values, record.id),
						save: (values?: T, saveRequest?: string) => handleSave(values || record, false, saveRequest),
						updateAndSave: (values: Partial<T>, saveRequest?: string) => handleSave(
							{
								...record, ...values, id: record.id,
							},
							true,
							saveRequest,
						),
						setExpanded: (value: boolean) => setExpandedRows((prev) => {
							const key = getKey(record);
							return value
								? prev.includes(key) ? prev : prev.concat(key)
								: prev.filter((id) => id !== key);
						}),
						reload: () => reload({ current: pagination.current }),
						getDefaultAction: () => <></>,
						validationWrapper: getValidationWrapper(item || record),
						add: addItem,
					};
					return expandedRender({
						...props,
						updateAndSave: props.updateAndSave,
						getDefaultAction: (actions) => showActions(actions, record, props, true),
						item: Edit(record) || record,
						readonly: typeof Edit(record) === 'undefined',
						setValue: (key: string, value) => {
							setItemPropValue(record.id, key, value);
						},
						expand: props.setExpanded,
					});
				},
				expandedRowKeys: expandedRows,
				expandIcon: (props) => _expandIcon(props, Edit(props.record) || props.record),
				onExpand: (_expanded: boolean, record: T) => {
					const key = getKey(record);
					setExpandedRows((prevState) => (prevState.includes(key) ? prevState.filter((k) => k !== key) : [...prevState, key]));
				},
				columnWidth: expandableColumnWidth,
				fixed: expandableFixed,
				columnTitle: expandableColumnTitle,
			},
		};
	}
	const table = (<Table
		{...tableProps}
		rowSelection={withSelectedRow ? { ...rowSelection, selections: undefined } : undefined}
		pagination={false}
		rowKey={(record) => getKey(record)}
	/>);

	return <>
		{onResize
			? (
				<ResizeObserverContainer onResize={onResize}>
					{table}
				</ResizeObserverContainer>
			)
			: table}
		<div className="clearfix editable-table-pagination">
			{!hidePagination && <div className="pull-right">
				<Pagination
					{...paginationProps}
					itemRender={(...props) => itemRender(tableId || loadRequest || `${key}`)(...props)}
					onChange={onPaginationChange}
					{...pagination}
					pageSizeOptions={pageSizeOptions}
					showSizeChanger={showSizeChanger}
				/>
			</div>}
			{withTotal && pagination.total > 0 ? <div className="pull-left ml20 mt10">
				Total:
				{' '}
				{pagination.total}
			</div> : <></>}
			{withClearAll && items.length && removeAllRequest ? <a
				onClick={() => clearAllHandler()}
				className="pull-left ml10 mt10"
			>
				Clear All
			</a> : null}
			{withColumnSettings && <div className="mr10 pull-right">
				<CheckboxList
					buttonIcon={<i className="fa fa-cog" />}
					defaultValue={columnSettings}
					values={columnSettings}
					options={mergedColumns.filter(({ enable, hideCheckbox }) => (enable === undefined || enable) && !hideCheckbox)
						.map(({ title, settingsTitle, dataIndex }) => ({
							label: settingsTitle || title,
							value: dataIndex,
						}))
					}
					popoverProps={columnSettingsPopoverProps}
					onChange={(settings) => {
						mergedColumns.forEach(({ hideCheckbox, dataIndex }) => hideCheckbox && settings.push(dataIndex));
						onColumnSettingsChange && onColumnSettingsChange(settings);
						setSettings(settings);
					}}
				/>
			</div>}
		</div>
	</>;
};
